import { FILL_GRADIENT } from '@/constants/gradients'
import { motion, AnimatePresence } from 'framer-motion'

interface CreatingGameModalProps {
  isOpen: boolean
  progress?: number
  status?: string
  onComplete?: () => void
  onError?: (error: string) => void
}

const CreatingGameModal = ({ isOpen, progress = 0, status = 'Creating Game...' }: CreatingGameModalProps) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white dark:bg-gray-700 rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Create a New Game
                </h2>
              </div>

              {/* Loading State */}
              <div className="flex-1 flex flex-col items-center justify-center py-24 px-6">
                <div className="w-32 h-32 mb-8 relative scale-150">
                  {/* Fixed gradient background with mask - keeps gradient static */}
                  <div 
                    className="absolute inset-0"
                    style={{
                      background: FILL_GRADIENT,
                      WebkitMaskImage: 'url(#spinnerMask)',
                      maskImage: 'url(#spinnerMask)',
                      backgroundSize: '75%',
                      backgroundPosition: 'center',
                      WebkitMaskSize: 'contain',
                      maskSize: 'contain',
                    }}
                  />
                  
                  {/* SVG mask definition - invisible but defines the mask shape */}
                  <svg width="0" height="0" style={{ position: 'absolute' }}>
                    <defs>
                      <mask id="spinnerMask">
                        <rect width="100%" height="100%" fill="black" />
                        <g transform="translate(64, 64)">
                          <motion.g
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          >
                            <motion.circle
                              r="40"
                              stroke="white"
                              strokeWidth="8"
                              fill="none"
                              strokeLinecap="round"
                              animate={{ strokeDasharray: ["40 251", "120 251", "40 251"] }}
                              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                            />
                          </motion.g>
                        </g>
                      </mask>
                    </defs>
                  </svg>
                  
                  {/* Progress percentage in center */}
                  {progress > 0 && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      <span 
                        className="text-2xl font-medium"
                        style={{
                          background: 'linear-gradient(270deg, #FF00CF 0%, #FCB902 100%)',
                          WebkitBackgroundClip: 'text',
                          backgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          color: 'transparent',
                        }}
                      >
                        {progress}%
                      </span>
                    </div>
                  )}
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                  {status}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-center">
                  {progress === 100 ? 'Almost done!' : 'Please wait while we set up your project'}
                </p>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default CreatingGameModal


